import { AnchoredLayer, AnchoredLayerAnimation } from '@doordash/component-layer-manager';
import React from 'react';
import ToastsContainer from './components/ToastsContainer.js';
import ViewportConstrainedToasts from './components/ViewportConstrainedToasts.js';
import '@doordash/design-language';
import { ToastPosition } from './constants.js';
import './styles.js';
import '@doordash/component-button';
import '@doordash/component-loading';
import { ToastDuration } from './types.js';
import { updateToastsToRender } from './utils.js';
import ToastContext from './ToastContext.js';
import { generateUniqueId } from '@doordash/utilities';

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
var useToastProvider = function useToastProvider(_ref) {
  var globalDuration = _ref.duration,
      onDisplay = _ref.onDisplay,
      globalOnHide = _ref.onHide,
      _ref$toastLimit = _ref.toastLimit,
      toastLimit = _ref$toastLimit === void 0 ? 3 : _ref$toastLimit;

  var _React$useState = React.useState({}),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      toasts = _React$useState2[0],
      setToasts = _React$useState2[1]; // We want the values set in `removeToast`
  // and `addToast` to have up to date values
  // so we use a ref to have an object that
  // persists during the lifecycle of the
  // component, and can hold the updated
  // toasts dictionary.


  var toastsRef = React.useRef(toasts);
  toastsRef.current = toasts; // Also, we need to save a ref to
  // keep track of all the timeouts
  // for hiding the toasts we have
  // so that we can cancel then on unmount
  // preventing state changes on an unmounted
  // component.

  var timeoutsRef = React.useRef({});

  var removeToast = function removeToast(toastId) {
    // if this is being called
    // outside of the timeout,
    // make sure the timeout
    // is cancelled before being fired
    clearTimeout(timeoutsRef.current[toastId]); // remove toast from object, save props
    // and set in state to update display

    var newToasts = _objectSpread({}, toastsRef.current);

    var toastProps = newToasts[toastId];
    delete newToasts[toastId];
    setToasts(newToasts);

    if (toastProps && toastProps.onHide) {
      toastProps.onHide(toastId, toastProps);
    }

    if (toastProps && globalOnHide) {
      globalOnHide(toastId, toastProps);
    }
  };

  var addToast = function addToast(_ref2) {
    var duration = _ref2.duration,
        toastProps = _objectWithoutProperties(_ref2, ["duration"]);

    // Limit to only three toasts; if over, id returned is null
    if (Object.entries(toastsRef.current).length >= toastLimit) return null; // Create a new ID to reference the toast,
    // set toast props into state for display

    var generatedId = generateUniqueId();
    setToasts(_objectSpread(_objectSpread({}, toastsRef.current), {}, _defineProperty({}, generatedId, toastProps)));

    if (onDisplay) {
      onDisplay(generatedId, toastProps);
    }

    var autoCloseTime = duration || globalDuration;

    if (autoCloseTime && autoCloseTime > 0) {
      // Schedule hiding the toast based on configured
      // duration or globalDuration using a timeout
      var timeoutId = setTimeout(function () {
        return removeToast(generatedId);
      }, autoCloseTime); // Save the timeout id
      // so that we can clear any
      // outstanding timeouts on unmount

      timeoutsRef.current[generatedId] = timeoutId;
    }

    return generatedId;
  }; // Clean up any outstanding timeouts
  // from the ref to prevent state changes
  // on an unmounted component


  React.useEffect(function () {
    // Storing timeout ref in a variable per linting warning
    // react-hooks/exhaustive-deps -> The ref value 'timeoutsRef.current' will likely have changed by the time this effect cleanup function runs
    var timeouts = timeoutsRef.current;
    return function () {
      Object.values(timeouts).map(clearTimeout);
    };
  }, []);
  return {
    toasts: toasts,
    addToast: addToast,
    removeToast: removeToast
  };
};

var ToastProvider = function ToastProvider(_ref3) {
  var children = _ref3.children,
      _ref3$position = _ref3.position,
      position = _ref3$position === void 0 ? ToastPosition.BottomCenter : _ref3$position,
      _ref3$duration = _ref3.duration,
      duration = _ref3$duration === void 0 ? ToastDuration.Short : _ref3$duration,
      onDisplay = _ref3.onDisplay,
      onHide = _ref3.onHide,
      setContainerAsViewport = _ref3.setContainerAsViewport;

  var _useToastProvider = useToastProvider({
    duration: duration,
    onDisplay: onDisplay,
    onHide: onHide
  }),
      toastsToDisplay = _useToastProvider.toasts,
      addToast = _useToastProvider.addToast,
      removeToast = _useToastProvider.removeToast; // toastsToRender is a copy of toastsToDisplay that is kept to allow the
  // ToastsContainer "LayerAnimation" component enough time to fully run the
  // exiting animation before deleting the Toast from the DOM and any
  // stateful records.


  var _React$useState3 = React.useState({}),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      toastsToRender = _React$useState4[0],
      setToastsToRender = _React$useState4[1];

  React.useEffect(function () {
    updateToastsToRender({
      toastsToDisplay: toastsToDisplay,
      toastsToRender: toastsToRender,
      setToastsToRender: setToastsToRender
    });
  }, [toastsToDisplay, toastsToRender, setToastsToRender]);

  if (setContainerAsViewport) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ToastContext.Provider, {
      value: {
        displayToast: addToast,
        hideToast: removeToast
      }
    }, children), /*#__PURE__*/React.createElement(ViewportConstrainedToasts, {
      toastsToDisplay: toastsToDisplay,
      toastsToRender: toastsToRender,
      setToastsToRender: setToastsToRender,
      position: position
    }));
  }

  return /*#__PURE__*/React.createElement(AnchoredLayer, {
    animation: AnchoredLayerAnimation.Custom,
    content: Object.keys(toastsToRender).length ? /*#__PURE__*/React.createElement(ToastsContainer, {
      toastsToDisplay: toastsToDisplay,
      position: position,
      toastsToRender: toastsToRender,
      setToastsToRender: setToastsToRender
    }) : undefined,
    inner: true,
    position: position,
    shouldAutomaticallyFlip: true
  }, /*#__PURE__*/React.createElement(ToastContext.Provider, {
    value: {
      displayToast: addToast,
      hideToast: removeToast
    }
  }, children));
};

ToastProvider.Positions = ToastPosition;
ToastProvider.Durations = ToastDuration;

export { ToastProvider as default, useToastProvider };
